// Explicit request (EnquiryFormDrawer split): the pure enquiry <-> form-values mapping that used to live inside the
// drawer component. Everything here is a plain function of its arguments (no hooks, no component state), so it is
// easy to read, test and reuse, and moving it did not change behaviour or what the drawer renders.
import moment from 'moment';
import { DocumentFile } from '../../types/DocumentFile';
import { CustomFieldKeyProps } from '../../enquiryCustomFields/common/getField';
import { FieldType } from '../../enquiryCustomFields/selectors/CustomFieldTypeSelector';
import { EnquiryDefaultConfig } from '../EnquirySummaryRepository';
import { addBusinessDays } from '../../helpers/commonHelper';
import { FormApi } from 'final-form';
import { formatDateString } from '../../helpers/DateHelper';
import { EnquiryDetail } from '../models/Enquiry';
import { IndividualNameSummary } from '../selectors/IndividualSelector';
import { OrganisationIAutoCompleteItem } from '../selectors/OrganisationSelector';
import { IAutoCompleteItem } from '../../typings/autoComplete';
import { UserFeature, UserFeatureEnum } from '../../types/UserFeature';
import { EventsAndCampaignIAutoCompleteItem } from '../../notesSummary/selectors/EventsAndCampaignSelector';
import { getActivitiesTabFields, getDetailsTabFields } from './enquiryFormDrawerConstants';
import type { EnquiryCustomFieldMutation, Enquiry, EnquiryFormProps } from './EnquiryFormDrawer';

export const getFiles = (files: DocumentFile[]) => {
    return files.map((document: DocumentFile) => {
        return {
            file: document.file,
            name: document.name,
            guidID: document.guidID,
            status: document.status
        };
    });
};

export const deepEqual = (obj1: any, obj2: any) => {
    if (obj1 === obj2) return true;
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 == null || obj2 == null) return false;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
        if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) return false;
    }

    return true;
};

export const reverseCustomFieldConversion = (fieldValues: EnquiryCustomFieldMutation[]) => {
    const customField: { [key: string]: CustomFieldKeyProps } = {};

    fieldValues.forEach(field => {
        let customFieldType = 0; // Default to Text

        if (field.boolValue !== null) customFieldType = FieldType['Yes/No'];
        else if (field.dateValue !== null) customFieldType = FieldType.Date;
        else if (field.currencyValue !== null) customFieldType = FieldType.Currency;
        else if (field.dateTimeValue !== null) customFieldType = FieldType['Date/Time'];
        else if (field.integerValue !== null) customFieldType = FieldType.Integer;
        else if (field.multiLineTextValue !== null) customFieldType = FieldType['Multi Line'];
        else if (field.numberValue !== null) customFieldType = FieldType.Number;
        else if (field.textValue !== null) customFieldType = FieldType.Text;
        else if (field.customFieldLookupListItemGuid !== null) customFieldType = FieldType['Lookup List']
        else if (field.emailValue !== null) customFieldType = FieldType.Email; // Added Email type

        if (!field.customFieldGuid) return;

        let customFieldValue = field.boolValue ?? field.dateValue ?? field.currencyValue ?? field.dateTimeValue ?? field.integerValue ?? field.multiLineTextValue ?? field.numberValue ?? field.textValue ?? field.emailValue;

        if (field.customFieldLookupListItemGuid && field.lookupListName) {
            customFieldValue = {
                label: field.lookupListName,
                value: field.customFieldLookupListItemGuid
            } as any;
        }

        customField[field.customFieldGuid] = {
            customFieldGuid: field.customFieldGuid,
            customFieldGroupGuid: field.customFieldGroupGuid || '', // Placeholder, replace with actual value if available
            customFieldId: "", // Placeholder, replace with actual value if available
            customFieldType: customFieldType,
            customFieldValue: customFieldValue
        };
    });

    return customField;
};

export const getFollowUpDueDefaultValue = (enquiryDefaultConfig: EnquiryDefaultConfig | undefined) => {
    // check outcome date (FollowUpDue)
    let outcomeDate: Date | null = null;

    if (enquiryDefaultConfig) {

        let hours = 0;
        let minutes = 0;
        let validTimeOfDay = false;

        if (enquiryDefaultConfig.timeOfDay && enquiryDefaultConfig.timeOfDay.toLowerCase() !== 'invalid date') {
            var timeOfDaySplit = enquiryDefaultConfig.timeOfDay ? enquiryDefaultConfig.timeOfDay.split(':') : null;

            if (timeOfDaySplit) {
                validTimeOfDay = true;
                hours = Number(timeOfDaySplit[0]);
                minutes = Number(timeOfDaySplit[1]);
            }
        }

        if (enquiryDefaultConfig.isCalculatedDays) {
            let date = addBusinessDays(moment().toDate(), enquiryDefaultConfig.addDays ? enquiryDefaultConfig.addDays : 0);

            if (validTimeOfDay) {
                outcomeDate = date
                    .set('hour', hours)
                    .set('minute', minutes)
                    .toDate();
            } else {
                outcomeDate = date.toDate();
            }
        } else if (enquiryDefaultConfig.isCalculatedHours) {
            let addhours = enquiryDefaultConfig.addHours ? enquiryDefaultConfig.addHours : 0;

            outcomeDate = moment()
                .add(addhours, 'hour')
                .toDate();
        }
    }

    return outcomeDate;
};

export const detectUpdatedTab = (form: FormApi<Enquiry>, formValues: any, initialValues: any) => {
    const allTabFields = {
        detailsTab: getDetailsTabFields(),
        activityTab: getActivitiesTabFields(),
    };

    let updatedTab: string | undefined = undefined;

    for (const [tab, fields] of Object.entries(allTabFields)) {
        if (fields.some((field) => {
            const fv = formValues[field];
            const iv = initialValues[field];
            if (field === 'customField') {
                return !deepEqual(fv, iv);
            }
            // Compare only date part for initialEnquiryDate
            if (field === 'initialEnquiryDate' && fv && iv) {
                const fvDate = new Date(fv);
                const ivDate = new Date(iv);
                return (
                    fvDate.getFullYear() !== ivDate.getFullYear() ||
                    fvDate.getMonth() !== ivDate.getMonth() ||
                    fvDate.getDate() !== ivDate.getDate()
                );
            }
            // Deep compare objects (e.g., source, office, etc.)
            if (
                typeof fv === 'object' &&
                fv !== null &&
                typeof iv === 'object' &&
                iv !== null
            ) {
                return !deepEqual(fv, iv);
            }
            // Fallback for primitives
            return fv !== iv;
        })) {
            updatedTab = tab;
            break;
        }
    }

    form.change('tabEdited', updatedTab as any);
};

export const createEnquiryInput = (values: Enquiry, isEmailNotification: boolean) => {
    // Set not proceeding values
    let followUpAction = values.followUpAction;
    let followUpActionSystemName = values.followUpActionSystemName;
    let reason = values.reason;
    let reasonGuid = values.reasonGuidID;
    let outcome = values.outcome;
    let outcomeDate = /*values.outcome === 'New Client' || values.outcome === 'New Matter' ||*/ values.outcome === 'Proceeding' || values.outcome === 'Not Proceeding'
        ? values.followUpDate : values.outcomeDate;
    let outcomeNotes = values.outcomeNotes;
    let followUpDueDate = values.assignedTo ? values.followUpDueDate : null;

    if (values.isNotProceeding) {
        followUpAction = null; // If not proceeding on creation, there is no follow up action.
        followUpActionSystemName = null;
        reason = values.notProceedingReason;
        reasonGuid = values.notProceedingReasonGuid;
        outcome = 'Not Proceeding';
        outcomeDate = moment().toDate();
        outcomeNotes = '';
        // FollowUpDue in Assignment is not mandatory if not proceeding. But, if we set the value, then use that. Or else pass a default value
        followUpDueDate = followUpDueDate ? followUpDueDate : moment().toDate();
    }

    let clientDobFinal = formatDateString(values.dOB);
    let secondClientDobFinal = formatDateString(values.secondClientDOB);
    let otherPartyDobFinal = formatDateString(values.otherPartyDOB);

    const tempCustomValues: CustomFieldKeyProps[] = values.customField ? Object.values(values.customField as CustomFieldKeyProps) : [];

    const tempcustomFieldValues: EnquiryCustomFieldMutation[] = tempCustomValues.map((custom: CustomFieldKeyProps) => {

        const dateValueFinal = custom.customFieldType === FieldType.Date ? formatDateString(custom.customFieldValue) : null;

        return {
            entityType: 1,
            entityGuid: null,
            customFieldGuid: custom.customFieldGuid,
            // customFieldGroupGuid: custom.customFieldGroupGuid,
            boolValue: custom.customFieldType === FieldType['Yes/No'] ? custom.customFieldValue : null,
            dateValue: dateValueFinal as any,
            currencyValue: custom.customFieldType === FieldType.Currency ? custom.customFieldValue : null,
            dateTimeValue: custom.customFieldType === FieldType['Date/Time'] ? custom.customFieldValue : null,
            integerValue: custom.customFieldType === FieldType.Integer ? custom.customFieldValue : null,
            multiLineTextValue: custom.customFieldType === FieldType['Multi Line'] ? custom.customFieldValue : null,
            numberValue: custom.customFieldType === FieldType.Number ? custom.customFieldValue : null,
            textValue: custom.customFieldType === FieldType.Text ? custom.customFieldValue : null,
            customFieldLookupListItemGuid: custom.customFieldType === FieldType['Lookup List'] && custom.customFieldValue ? custom.customFieldValue.value : null,
            emailValue: custom.customFieldType === FieldType.Email ? custom.customFieldValue : null,
        };
    });

    return {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        gender: values.gender ? values.gender : 'Unknown',
        initialEnquiryDate: values.initialEnquiryDate,
        phone: values.phone,
        mobile: values.mobile,
        source: values.source!.label,
        office: values.office && values.office.label ? values.office.label : null,
        sourceNotes: values.sourceNotes,
        organisation: values.organisation,
        positionAtOrganisation: values.positionAtOrganisation,
        natureOfEnquiry: values.natureOfEnquiry,
        // practiceAreaGuidID: values.practiceAreaGuidID,
        areaOfLaw: values.areaOfLaw,
        internalNotes: values.internalNotes,
        files: getFiles(values.files),

        // Assignment
        assignedTo: values.assignedTo,
        followUpDueDate: followUpDueDate,
        assignedBy: values.assignedBy,
        dateAssigned: values.dateAssigned,
        reminderOffset: values.assignedTo
            ? values.assignmentFollowUpDueReminder ? values.assignmentFollowUpDueReminder.value : null
            : null,

        // Follow Up
        followUpAction: followUpAction,
        followUpActionSystemName: followUpActionSystemName,
        followUpDate: values.followUpAction ? values.followUpDate : null, // Date in Follow Up
        outcome: outcome,
        outcomeDate: outcomeDate,
        outcomeNotes: outcomeNotes,
        reason: reason,
        reasonGuid: reasonGuid,
        followUpNotes: values.followUpNotes,
        referredToFirmName: values.referredToFirmNameInProceeding,
        referredToFirmGuid: values.referredToFirmGuidInProceeding,
        notProceedingNotes: values.notProceedingOutcomeNotesInProceeding,

        sendNotification: isEmailNotification,
        isKeyOpportunity: values.isKeyOpportunity,
        referrerGuid: values.referrerGuid,
        isReferrerFromDataSource: values.isReferrerFromDataSource,
        nameGuid: values.nameGuid,
        organisationGuid: values.organisationGuid,

        isUpdateContacts: values.isUpdateContacts,
        isSendEnquiryResponse: values.isSentAcknowledgementEmail,

        estimatedFees: values.estimatedFees,
        isEstimatedFeesIncludesTax: values.isEstimatedFeesIncludesTax,
        estimatedDisbursements: values.estimatedDisbursements,
        isEstimatedDisbursementsIncludesTax: values.isEstimatedDisbursementsIncludesTax,
        enquiryQuality: values.enquiryQuality,
        likelihoodToProceed: values.likelihoodToProceed,
        clientFirstName: values.clientFirstName,
        clientLastName: values.clientLastName,
        dOB: clientDobFinal,
        isMarketingConsent: values.isMarketingConsent,
        isConflictCheckPerformed: values.isConflictCheckPerformed,
        isTermsAndConditionsApproved: values.isTermsAndConditionsApproved,
        isIdentityCheckPerformed: values.isIdentityCheckPerformed,
        isPrivacyStatementProvided: values.isPrivacyStatementProvided,
        addressName: values.addressName,
        floor: values.floor,
        number: values.number,
        street: values.street,
        clientAddressStreet2: values.street2,
        suburb: values.suburb,
        state: values.state,
        postcode: values.postcode,
        country: values.country,
        pOBoxType: values.pOBox ? values.pOBoxType ? values.pOBoxType : null : null,
        pOBox: values.pOBox,
        postalSuburb: values.postalSuburb,
        postalState: values.postalState,
        postalPostCode: values.postalPostCode,
        postalCountry: values.postalCountry,
        isNoConflict: values.isNoConflict,
        clientRelationshipToEnquirer: values.clientRelationshipToEnquirer,
        otherPartyFirstName: values.otherPartyFirstName,
        otherPartyLastName: values.otherPartyLastName,
        otherPartyNotes: values.otherPartyNotes,
        otherPartyEmail: values.otherPartyEmail,
        otherPartyPhone: values.otherPartyPhone,
        otherPartyMobile: values.otherPartyMobile,
        otherPartyOrganisation: values.otherPartyOrganisation,
        enquiryNotes: values.enquiryNotes,
        otherPartyDOB: otherPartyDobFinal,
        otherPartyAddressName: values.otherPartyAddressName,
        otherPartyFloor: values.otherPartyFloor,
        otherPartyNumber: values.otherPartyNumber,
        otherPartyStreet: values.otherPartyStreet,
        otherPartyStreet2: values.otherPartyStreet2,
        otherPartySuburb: values.otherPartySuburb,
        otherPartyState: values.otherPartyState,
        otherPartyPostcode: values.otherPartyPostcode,
        otherPartyCountry: values.otherPartyCountry,

        otherPartyPOBoxType: values.otherPartyPOBoxType,
        otherPartyPOBox: values.otherPartyPOBox,
        otherPartyPostalSuburb: values.otherPartyPostalSuburb,
        otherPartyPostalState: values.otherPartyPostalState,
        otherPartyPostalPostCode: values.otherPartyPostalPostCode,
        otherPartyPostalCountry: values.otherPartyPostalCountry,
        otherPartyGuid: values.otherPartyNameGuid,
        otherPartyOrganisationGuid: values.otherPartyOrganisationGuid,

        isRiskAssessed: values.isRiskAssessed,
        riskLevel: values.isRiskAssessed ? values.riskLevelSelector ? values.riskLevelSelector.label : null : null,
        riskNotes: values.isRiskAssessed ? values.riskNotes : null,

        secondClientFirstName: values.secondClientFirstName,
        secondClientLastName: values.secondClientLastName,
        secondClientRelationshipToEnquirer: values.secondClientRelationshipToEnquirer,
        secondClientGender: values.secondClientGender ?? 'Unknown',
        secondClientSuburb: values.secondClientSuburb,
        secondClientState: values.secondClientState,
        secondClientPostCode: values.secondClientPostCode,
        secondClientIsMarketingConsent: values.secondClientIsMarketingConsent,
        secondClientIsTermsAndConditionsApproved: values.secondClientIsTermsAndConditionsApproved,
        secondClientIsPrivacyStatementProvided: values.secondClientIsPrivacyStatementProvided,
        secondClientIsConflictCheckPerformed: values.secondClientIsConflictCheckPerformed,
        secondClientIsIdentityCheckPerformed: values.secondClientIsIdentityCheckPerformed,
        secondClientIsNoConflict: values.secondClientIsNoConflict,
        secondClientCountry: values.secondClientCountry,
        secondClientAddressName: values.secondClientAddressName,
        secondClientFloor: values.secondClientFloor,
        secondClientNumber: values.secondClientNumber,
        secondClientStreet1: values.secondClientStreet1,
        secondClientStreet2: values.secondClientStreet2,
        secondClientDOB: secondClientDobFinal,
        secondClientPOBoxType: values.secondClientPOBoxType,
        secondClientPOBox: values.secondClientPOBox,
        secondClientPostalSuburb: values.secondClientPostalSuburb,
        secondClientPostalState: values.secondClientPostalState,
        secondClientPostalPostCode: values.secondClientPostalPostCode,
        secondClientPostalCountry: values.secondClientPostalCountry,

        responseText: values.responseText,

        // events and campaign
        eventAndCampaignGuid: values.eventAndCampaignSelector ? values.eventAndCampaignSelector.value : null,
        eventAndCampaignNotes: values.eventsAndCampaignNotes,

        isNameFromDataSource: values.individualSelector?.isFromDataSource ?? null,
        // tslint:disable-next-line: max-line-length
        isOrganisationFromDataSource: values.organisationSelector?.isFromDataSource ?? (values.individualSelector?.organisationName !== null ? values.individualSelector?.isFromDataSource : null),
        isOtherSideFromDataSource: values.otherPartyIndividualSelector?.isFromDataSource ?? null,
        isSecondClientFromDataSource: values.secondClientIndividualSelector?.isFromDataSource ?? null,

        title: values.title,
        clientTitle: values.clientTitle,
        secondClientTitle: values.secondClientTitle,
        otherPartyTitle: values.otherPartyTitle,
        clientGuid: values.clientGuid,
        clientEmail: values.clientEmail,
        clientPhone: values.clientPhone,
        clientMobile: values.clientMobile,
        secondClientGuid: values.secondClientGuid,
        secondClientEmail: values.secondClientEmail,
        secondClientPhone: values.secondClientPhone,
        secondClientMobile: values.secondClientMobile,

        customFields: tempcustomFieldValues,

        clickThroughUrl: values.clickThroughUrl,
        utmSource: values.utmSource,
        utmMedium: values.utmMedium,
        utmCampaign: values.utmCampaign,
        utmContent: values.utmContent,
        utmKeyword: values.utmKeyword,
        googleClickIdentifier: values.googleClickIdentifier,
        facebookClickIdentifier: values.facebookClickIdentifier
    };
};

export const updateEnquiryInput = (values: Enquiry, updatedValues: string, isEmailNotification: boolean) => {

    let clientDobFinal = formatDateString(values.dOB);
    let secondClientDobFinal = formatDateString(values.secondClientDOB);
    let otherPartyDobFinal = formatDateString(values.otherPartyDOB);

    const tempCustomValues: CustomFieldKeyProps[] = values.customField ? Object.values(values.customField as CustomFieldKeyProps) : [];

    const tempcustomFieldValues: EnquiryCustomFieldMutation[] = tempCustomValues.map((custom: CustomFieldKeyProps) => {

        const dateValueFinal = custom.customFieldType === FieldType.Date ? formatDateString(custom.customFieldValue) : null;

        return {
            entityType: 1,
            entityGuid: null,
            customFieldGuid: custom.customFieldGuid,
            boolValue: custom.customFieldType === FieldType['Yes/No'] ? custom.customFieldValue : null,
            dateValue: dateValueFinal as any,
            currencyValue: custom.customFieldType === FieldType.Currency ? custom.customFieldValue : null,
            dateTimeValue: custom.customFieldType === FieldType['Date/Time'] ? custom.customFieldValue : null,
            integerValue: custom.customFieldType === FieldType.Integer ? custom.customFieldValue : null,
            multiLineTextValue: custom.customFieldType === FieldType['Multi Line'] ? custom.customFieldValue : null,
            numberValue: custom.customFieldType === FieldType.Number ? custom.customFieldValue : null,
            textValue: custom.customFieldType === FieldType.Text ? custom.customFieldValue : null,
            customFieldLookupListItemGuid: custom.customFieldType === FieldType['Lookup List'] && custom.customFieldValue ? custom.customFieldValue.value : null,
            emailValue: custom.customFieldType === FieldType.Email ? custom.customFieldValue : null,
            // customFieldGroupGuid: null
        };
    });

    return {
        guid: values.guid,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        gender: values.gender ? values.gender : 'Unknown',
        initialEnquiryDate: values.initialEnquiryDate,
        phone: values.phone,
        mobile: values.mobile,
        source: values.source!.label,
        office: values.office && values.office.label ? values.office.label : null,
        sourceNotes: values.sourceNotes,
        organisation: values.organisation,
        positionAtOrganisation: values.positionAtOrganisation,
        natureOfEnquiry: values.natureOfEnquiry,
        areaOfLaw: values.areaOfLaw,
        internalNotes: values.internalNotes,
        files: getFiles(values.files),

        assignedTo: values.assignedTo,
        followUpDueDate: values.assignedTo ? values.followUpDueDate : null,
        assignedBy: values.assignedBy,
        dateAssigned: values.dateAssigned,
        reminderOffset: values.assignedTo
            ? values.assignmentFollowUpDueReminder ? values.assignmentFollowUpDueReminder.value : null
            : null,

        followUpAction: null, // values.followUpAction,
        followUpActionSystemName: null,
        followUpDate: null, // values.followUpAction ? values.followUpDate : null,
        outcome: null, // values.outcome,
        outcomeDate: null, // values.outcome === 'New Client' || values.outcome === 'Not Proceeding' ? values.followUpDate : values.outcomeDate,
        outcomeNotes: null,
        reason: null, // values.reason,
        reasonGuid: null, // values.reasonGuidID,
        followUpNotes: null, // values.followUpNotes,

        updatedValues: updatedValues,
        sendNotification: isEmailNotification,
        isKeyOpportunity: values.isKeyOpportunity,
        referrerGuid: values.referrerGuid,
        isReferrerFromDataSource: values.isReferrerFromDataSource,
        nameGuid: values.nameGuid,
        organisationGuid: values.organisationGuid,

        isUpdateContacts: values.isUpdateContacts,
        isSendEnquiryResponse: values.isSentAcknowledgementEmail,

        estimatedFees: values.estimatedFees,
        isEstimatedFeesIncludesTax: values.isEstimatedFeesIncludesTax,
        estimatedDisbursements: values.estimatedDisbursements,
        isEstimatedDisbursementsIncludesTax: values.isEstimatedDisbursementsIncludesTax,
        enquiryQuality: values.enquiryQuality,
        likelihoodToProceed: values.likelihoodToProceed,
        clientFirstName: values.clientFirstName,
        clientLastName: values.clientLastName,
        dOB: clientDobFinal,
        isMarketingConsent: values.isMarketingConsent,
        isConflictCheckPerformed: values.isConflictCheckPerformed,
        isTermsAndConditionsApproved: values.isTermsAndConditionsApproved,
        isIdentityCheckPerformed: values.isIdentityCheckPerformed,
        isPrivacyStatementProvided: values.isPrivacyStatementProvided,
        addressName: values.addressName,
        floor: values.floor,
        number: values.number,
        street: values.street,
        clientAddressStreet2: values.street2,
        suburb: values.suburb,
        state: values.state,
        postcode: values.postcode,
        country: values.country,
        pOBoxType: values.pOBox ? values.pOBoxType ? values.pOBoxType : null : null,
        pOBox: values.pOBox,
        postalSuburb: values.postalSuburb,
        postalState: values.postalState,
        postalPostCode: values.postalPostCode,
        postalCountry: values.postalCountry,
        isNoConflict: values.isNoConflict,
        clientRelationshipToEnquirer: values.clientRelationshipToEnquirer,
        otherPartyFirstName: values.otherPartyFirstName,
        otherPartyLastName: values.otherPartyLastName,
        otherPartyNotes: values.otherPartyNotes,
        otherPartyEmail: values.otherPartyEmail,
        otherPartyPhone: values.otherPartyPhone,
        otherPartyMobile: values.otherPartyMobile,
        otherPartyOrganisation: values.otherPartyOrganisation,
        enquiryNotes: values.enquiryNotes,
        otherPartyDOB: otherPartyDobFinal,
        otherPartyAddressName: values.otherPartyAddressName,
        otherPartyFloor: values.otherPartyFloor,
        otherPartyNumber: values.otherPartyNumber,
        otherPartyStreet: values.otherPartyStreet,
        otherPartyStreet2: values.otherPartyStreet2,
        otherPartySuburb: values.otherPartySuburb,
        otherPartyState: values.otherPartyState,
        otherPartyPostcode: values.otherPartyPostcode,
        otherPartyCountry: values.otherPartyCountry,

        otherPartyPOBoxType: values.otherPartyPOBoxType,
        otherPartyPOBox: values.otherPartyPOBox,
        otherPartyPostalSuburb: values.otherPartyPostalSuburb,
        otherPartyPostalState: values.otherPartyPostalState,
        otherPartyPostalPostCode: values.otherPartyPostalPostCode,
        otherPartyPostalCountry: values.otherPartyPostalCountry,
        otherPartyGuid: values.otherPartyNameGuid,
        otherPartyOrganisationGuid: values.otherPartyOrganisationGuid,

        isRiskAssessed: values.isRiskAssessed,
        riskLevel: values.isRiskAssessed ? values.riskLevelSelector ? values.riskLevelSelector.label : null : null,
        riskNotes: values.isRiskAssessed ? values.riskNotes : null,

        secondClientFirstName: values.secondClientFirstName,
        secondClientLastName: values.secondClientLastName,
        secondClientRelationshipToEnquirer: values.secondClientRelationshipToEnquirer,
        secondClientGender: values.secondClientGender ?? 'Unknown',
        secondClientSuburb: values.secondClientSuburb,
        secondClientState: values.secondClientState,
        secondClientPostCode: values.secondClientPostCode,
        secondClientIsMarketingConsent: values.secondClientIsMarketingConsent,
        secondClientIsTermsAndConditionsApproved: values.secondClientIsTermsAndConditionsApproved,
        secondClientIsPrivacyStatementProvided: values.secondClientIsPrivacyStatementProvided,
        secondClientIsConflictCheckPerformed: values.secondClientIsConflictCheckPerformed,
        secondClientIsIdentityCheckPerformed: values.secondClientIsIdentityCheckPerformed,
        secondClientIsNoConflict: values.secondClientIsNoConflict,
        secondClientCountry: values.secondClientCountry,
        secondClientAddressName: values.secondClientAddressName,
        secondClientFloor: values.secondClientFloor,
        secondClientNumber: values.secondClientNumber,
        secondClientStreet1: values.secondClientStreet1,
        secondClientStreet2: values.secondClientStreet2,
        secondClientDOB: secondClientDobFinal,
        secondClientPOBoxType: values.secondClientPOBoxType,
        secondClientPOBox: values.secondClientPOBox,
        secondClientPostalSuburb: values.secondClientPostalSuburb,
        secondClientPostalState: values.secondClientPostalState,
        secondClientPostalPostCode: values.secondClientPostalPostCode,
        secondClientPostalCountry: values.secondClientPostalCountry,

        responseText: values.responseText,

        // events and campaign
        eventAndCampaignGuid: values.eventAndCampaignSelector ? values.eventAndCampaignSelector.value : null,
        eventAndCampaignNotes: values.eventsAndCampaignNotes,

        isNameFromDataSource: values.individualSelector?.isFromDataSource ?? null,
        isOrganisationFromDataSource: values.organisationSelector?.isFromDataSource ?? null,

        isOtherSideFromDataSource: values.otherPartyIndividualSelector?.isFromDataSource ?? null,
        isSecondClientFromDataSource: values.secondClientIndividualSelector?.isFromDataSource ?? null,

        title: values.title,
        clientTitle: values.clientTitle,
        secondClientTitle: values.secondClientTitle,
        otherPartyTitle: values.otherPartyTitle,
        clientGuid: values.clientGuid,
        clientEmail: values.clientEmail,
        clientPhone: values.clientPhone,
        clientMobile: values.clientMobile,
        secondClientGuid: values.secondClientGuid,
        secondClientEmail: values.secondClientEmail,
        secondClientPhone: values.secondClientPhone,
        secondClientMobile: values.secondClientMobile,

        customFields: tempcustomFieldValues,

        clickThroughUrl: values.clickThroughUrl,
        utmSource: values.utmSource,
        utmMedium: values.utmMedium,
        utmCampaign: values.utmCampaign,
        utmContent: values.utmContent,
        utmKeyword: values.utmKeyword,
        googleClickIdentifier: values.googleClickIdentifier,
        facebookClickIdentifier: values.facebookClickIdentifier,
    };
};

// What buildEnquiryFormValues needs from the drawer: the loaded enquiry plus the few props / flags it reads.
export interface BuildEnquiryFormValuesArguments {
    enquiryDetail: EnquiryDetail;
    isInitialLoad: boolean;
    isCreateForm: boolean;
    props: EnquiryFormProps;
    getReminderValue: (isAssignment: boolean) => { label: string; value: string };
}

// tslint:disable-next-line: no-any
export const buildEnquiryFormValues = ({ enquiryDetail, isInitialLoad, isCreateForm, props, getReminderValue }: BuildEnquiryFormValuesArguments): Enquiry => {
    let assignedBy = enquiryDetail.assignedBy;
    let assignedByName = enquiryDetail.assignedByName;
    let individualSelector: IndividualNameSummary | null = null;
    let clientIndividualSelector: IndividualNameSummary | null = null;
    let secondClientIndividualSelector: IndividualNameSummary | null = null;
    let organisationSelector: OrganisationIAutoCompleteItem | null = null;
    let referredToFirmSelector: IAutoCompleteItem | null = null;
    let otherPartyIndividualSelector: IndividualNameSummary | null = null;

    if (isInitialLoad && !isCreateForm) {
        if (assignedBy === null) {
            assignedBy = props.assignedBy;
            assignedByName = props.assignedByName;
        }
    }

    if (enquiryDetail.clientGuid) {
        clientIndividualSelector = {
            value: enquiryDetail.clientGuid,
            label: (enquiryDetail.clientFirstName + ' ' + enquiryDetail.clientLastName),
            id: 1,
            guidID: enquiryDetail.clientGuid,
            name: (enquiryDetail.clientFirstName + ' ' + enquiryDetail.clientLastName),
            firstName: enquiryDetail.clientFirstName,
            lastName: enquiryDetail.clientLastName,
            emailAddress: enquiryDetail.clientEmail,
            gender: null,
            mobile: enquiryDetail.clientMobile,
            phone: enquiryDetail.clientPhone,
            organisationGuid: null,
            organisationName: null,
            nameId: null,
            addressName: null,
            floor: null,
            number: null,
            street: null,
            city: null,
            state: null,
            postCode: null,
            country: null,
            isFromDataSource: null,
            title: enquiryDetail.clientTitle ?? 'none',
        };
    }

    if (enquiryDetail.secondClientGuid) {
        secondClientIndividualSelector = {
            value: enquiryDetail.secondClientGuid,
            label: (enquiryDetail.secondClientFirstName + ' ' + enquiryDetail.secondClientLastName),
            id: 1,
            guidID: enquiryDetail.secondClientGuid,
            name: (enquiryDetail.secondClientFirstName + ' ' + enquiryDetail.secondClientLastName),
            firstName: enquiryDetail.secondClientFirstName,
            lastName: enquiryDetail.secondClientLastName,
            emailAddress: enquiryDetail.secondClientEmail,
            gender: null,
            mobile: enquiryDetail.secondClientMobile,
            phone: enquiryDetail.secondClientPhone,
            organisationGuid: null,
            organisationName: null,
            nameId: null,
            addressName: null,
            floor: null,
            number: null,
            street: null,
            city: null,
            state: null,
            postCode: null,
            country: null,
            isFromDataSource: null,
            title: enquiryDetail.secondClientTitle ?? 'none',
        };
    }

    if (enquiryDetail.nameGuid) {
        individualSelector = {
            value: enquiryDetail.nameGuid,
            label: (enquiryDetail.firstName + ' ' + enquiryDetail.lastName),
            id: 1,
            guidID: enquiryDetail.nameGuid,
            name: (enquiryDetail.firstName + ' ' + enquiryDetail.lastName),
            firstName: enquiryDetail.firstName,
            lastName: enquiryDetail.lastName,
            emailAddress: enquiryDetail.email,
            gender: enquiryDetail.gender,
            mobile: enquiryDetail.mobile,
            phone: enquiryDetail.phone,
            organisationGuid: enquiryDetail.organisationGuid,
            organisationName: enquiryDetail.organisation,
            nameId: null,
            addressName: null,
            floor: null,
            number: null,
            street: null,
            city: null,
            state: null,
            postCode: null,
            country: null,
            isFromDataSource: enquiryDetail.isNameFromDataSource,
            title: enquiryDetail.title ?? 'none',
        };
    }

    if (enquiryDetail.otherPartyNameGuid) {
        otherPartyIndividualSelector = {
            value: enquiryDetail.otherPartyNameGuid,
            label: (enquiryDetail.otherPartyFirstName + ' ' + enquiryDetail.otherPartyLastName),
            id: 2,
            guidID: enquiryDetail.otherPartyNameGuid,
            name: (enquiryDetail.otherPartyFirstName + ' ' + enquiryDetail.otherPartyLastName),
            firstName: enquiryDetail.otherPartyFirstName,
            lastName: enquiryDetail.otherPartyLastName,
            emailAddress: enquiryDetail.otherPartyEmail,
            gender: null,
            mobile: enquiryDetail.otherPartyMobile,
            phone: enquiryDetail.otherPartyPhone,
            organisationGuid: enquiryDetail.organisationGuid,
            organisationName: enquiryDetail.otherPartyOrganisation,
            nameId: null,
            addressName: enquiryDetail.otherPartyAddressName,
            floor: enquiryDetail.otherPartyFloor,
            number: enquiryDetail.otherPartyNumber,
            street: enquiryDetail.otherPartyStreet,
            city: enquiryDetail.otherPartySuburb,
            state: enquiryDetail.otherPartyState,
            postCode: enquiryDetail.otherPartyPostcode,
            country: enquiryDetail.otherPartyCountry,
            isFromDataSource: enquiryDetail.isNameFromDataSource,
            title: enquiryDetail.otherPartyTitle ?? 'none',
        };
    }

    if (enquiryDetail.organisationGuid) {
        organisationSelector = { value: enquiryDetail.organisationGuid, label: enquiryDetail.organisation } as OrganisationIAutoCompleteItem;
    }

    if (enquiryDetail.referredToFirmGuid) {
        referredToFirmSelector = { value: enquiryDetail.referredToFirmGuid, label: enquiryDetail.referredToFirmName };
    }

    return {
        isBookMeetingEmailDialogOpen: false,
        guid: props.guid,
        isClosed: enquiryDetail.isClosed,
        firstName: enquiryDetail.firstName,
        lastName: enquiryDetail.lastName,
        individualSelector: individualSelector,
        email: enquiryDetail.email,
        gender: enquiryDetail.gender,
        initialEnquiryDate: enquiryDetail.initialEnquiryDate ? moment(enquiryDetail.initialEnquiryDate).toDate() : moment().toDate(),
        phone: enquiryDetail.phone,
        mobile: enquiryDetail.mobile,
        source: enquiryDetail.source && enquiryDetail.sourceGuidID ? { label: enquiryDetail.source, value: enquiryDetail.sourceGuidID } : null,
        office: enquiryDetail.office && enquiryDetail.officeGuidID ? { label: enquiryDetail.office, value: enquiryDetail.officeGuidID } : null,
        sourceNotes: enquiryDetail.sourceNotes,

        organisationSelector: organisationSelector,
        organisation: enquiryDetail.organisation,
        positionAtOrganisation: enquiryDetail.positionAtOrganisation,

        natureOfEnquiry: enquiryDetail.natureOfEnquiry,
        practiceAreaGuidID: enquiryDetail.practiceAreaGuidID,
        areaOfLaw: enquiryDetail.areaOfLaw,

        internalNotes: enquiryDetail.internalNotes,
        files: enquiryDetail.files.map(f => ({
            ...f,
            file: f.file || new File([], f.name)
        })),

        assignedTo: enquiryDetail.assignedTo,
        assignedToName: enquiryDetail.assignedToName,
        // assignedToEmailAddress: enquiryDetail.assignedToEmailAddress,
        assignedBy: assignedBy,
        assignedByName: assignedByName,
        // assignedByEmailAddress: enquiryDetail.assignedByEmailAddress,
        dateAssigned: enquiryDetail.dateAssigned === null ? moment().toDate() : moment(enquiryDetail.dateAssigned).toDate(),
        followUpDueDate: enquiryDetail.followUpDueDate === null ? null : moment(enquiryDetail.followUpDueDate).toDate(),
        // followUpDueDate: enquiryDetail.followUpDueDate === null
        //     ? assignedBy // When an enquiry is closed and re-opened, there will not be a due date.
        //                  // So the Follow Up Due in Assignment will be empty and cause validation errors. So, if we have a assignment value, then set it to today.
        //         ? (getFollowUpDueDefaultValue(props.enquiryDefaultConfig) == null ? moment().toDate() : getFollowUpDueDefaultValue(props.enquiryDefaultConfig))
        //         : null
        //     : moment(enquiryDetail.followUpDueDate).toDate(),
        assignmentFollowUpDueReminder: getReminderValue(true),

        followUpAction: 'Emailed', // enquiryDetail.followUpAction,
        followUpActionSystemName: 'Emailed',
        followUpSystemAction: '',
        followUpDate: moment().toDate(), // enquiryDetail.followUpDate === null ? moment().toDate() : moment(enquiryDetail.followUpDate).toDate(),
        outcome: 'More Follow Up', // enquiryDetail.outcome,y
        outcomeDate: getFollowUpDueDefaultValue(props.enquiryDefaultConfig), // null,
        outcomeNotes: '',
        reason: null, // enquiryDetail.reason,
        reasonGuidID: null,
        followUpNotes: null, // enquiryDetail.followUpNotes,
        hasFollowUpNotesMentions: false,
        followUpDueReminder: getReminderValue(false),
        autoFollowUpEmailTemplate: enquiryDetail.email && props.enquiryDefaultConfig && props.enquiryDefaultConfig.emailTemplateGuid && props.enquiryDefaultConfig.emailTemplateName ?
            {
                label: props.enquiryDefaultConfig.emailTemplateName,
                value: props.enquiryDefaultConfig.emailTemplateGuid
            } : null,
        isAutoFollowUp: enquiryDetail.email && props.enquiryDefaultConfig?.isAutoFollowUp ? props.enquiryDefaultConfig.isAutoFollowUp : false,

        handleCreateAndClose: false,
        handleCreateAndNew: false,
        handleUpdate: false,
        // showDiscard: false,
        // isFormChanged: false,
        isAssignmentExpand: enquiryDetail.assignedTo ? false : true,
        isKeyOpportunity: enquiryDetail.isKeyOpportunity,
        isNotProceeding: enquiryDetail.outcome === 'Not Proceeding' ? true : false,
        notProceedingReason: null,
        notProceedingReasonGuid: null,

        referrerGuid: enquiryDetail.referrerGuid,
        referrerName: enquiryDetail.referrerName,
        isReferrerFromDataSource: enquiryDetail.isReferrerFromDataSource ?? null,
        isShowReferrer: enquiryDetail.isSourceReferrer ? true : (enquiryDetail.referrerGuid ? true : false),

        isNameSwitched: false,
        isOrganisationSwitched: false, // enquiryDetail.organisationGuid ? true : false,
        nameGuid: enquiryDetail.nameGuid,
        organisationGuid: enquiryDetail.organisationGuid,

        // tslint:disable-next-line: max-line-length
        staff: null,
        startTime: null, // moment().add(1, 'hour').toDate(),
        endTime: null, // moment().add(2, 'hour').toDate(),
        isAllDay: false,
        officeLocation: null,
        feeApplicable: null,
        isSendConfirmation: true,
        meetingNotes: null,
        timezone: null,
        periodMeetingDefaultReminder: props.enquiryDefaultConfig?.periodMeetingDefaultReminder ? { label: props.enquiryDefaultConfig?.periodMeetingDefaultReminder, value: props.enquiryDefaultConfig?.periodMeetingDefaultReminder } : null,
        isSendMeetingDefaultReminderSms: enquiryDetail.mobile && UserFeature.HasAccess(UserFeatureEnum.hasSmsIntegration) && props.enquiryDefaultConfig?.isSendMeetingDefaultReminderSms ? props.enquiryDefaultConfig?.isSendMeetingDefaultReminderSms : false,
        isSendMeetingDefaultReminderEmail: enquiryDetail.email && props.enquiryDefaultConfig?.isSendMeetingDefaultReminderEmail ? props.enquiryDefaultConfig?.isSendMeetingDefaultReminderEmail : false,

        emailTemplate: null,

        isContactDetailsChanged: false,
        isUpdateContacts: false,
        isSentAcknowledgementEmail: false,
        updateFollowUpDueForInternalNote: true,
        outcomeDateChanged: false,

        // Other details
        estimatedFees: enquiryDetail.estimatedFees,
        isEstimatedFeesIncludesTax: enquiryDetail.isEstimatedFeesIncludesTax,
        estimatedDisbursements: enquiryDetail.estimatedDisbursements,
        isEstimatedDisbursementsIncludesTax: enquiryDetail.isEstimatedDisbursementsIncludesTax,
        enquiryQuality: enquiryDetail.enquiryQuality,
        likelihoodToProceed: enquiryDetail.likelihoodToProceed,

        clientFirstName: enquiryDetail.clientFirstName,
        clientLastName: enquiryDetail.clientLastName,
        dOB: enquiryDetail.dOB,
        isMarketingConsent: enquiryDetail.isMarketingConsent,
        isConflictCheckPerformed: enquiryDetail.isConflictCheckPerformed,
        isTermsAndConditionsApproved: enquiryDetail.isTermsAndConditionsApproved,
        isIdentityCheckPerformed: enquiryDetail.isIdentityCheckPerformed,
        isPrivacyStatementProvided: enquiryDetail.isPrivacyStatementProvided,
        addressName: enquiryDetail.addressName,
        floor: enquiryDetail.floor,
        number: enquiryDetail.number,
        street: enquiryDetail.street,
        street2: enquiryDetail.clientAddressStreet2,
        suburb: enquiryDetail.suburb,
        state: enquiryDetail.state,
        postcode: enquiryDetail.postcode,
        country: enquiryDetail.country,
        pOBoxType: enquiryDetail.pOBoxType ? enquiryDetail.pOBoxType : props.pOBoxType,
        pOBox: enquiryDetail.pOBox,
        postalSuburb: enquiryDetail.postalSuburb,
        postalState: enquiryDetail.postalState,
        postalPostCode: enquiryDetail.postalPostCode,
        postalCountry: enquiryDetail.postalCountry,
        isNoConflict: enquiryDetail.isNoConflict,
        isShowNotProceedingReferrer: enquiryDetail.referredToFirmGuid ? true : false,
        isNotProceedingReferrerSwitched: false,
        referredToFirmSelector: referredToFirmSelector,
        referredToFirmGuid: enquiryDetail.referredToFirmGuid,
        referredToFirmName: enquiryDetail.referredToFirmName,
        notProceedingOutcomeNotes: enquiryDetail.notProceedingOutcomeNotes,
        isShowNotProceedingReferrerInProceeding: enquiryDetail.referredToFirmGuid ? true : false,
        isNotProceedingReferrerSwitchedInProceeding: false,
        referredToFirmSelectorInProceeding: referredToFirmSelector,
        referredToFirmGuidInProceeding: enquiryDetail.referredToFirmGuid,
        referredToFirmNameInProceeding: enquiryDetail.referredToFirmName,
        notProceedingOutcomeNotesInProceeding: enquiryDetail.notProceedingOutcomeNotes,
        clientRelationshipToEnquirer: enquiryDetail.clientRelationshipToEnquirer,
        matterId: enquiryDetail.matterId,
        matterGuid: enquiryDetail.matterGuid,
        matterTitle: enquiryDetail.matterTitle,
        matterFileNumber: enquiryDetail.matterFileNumber,
        matterSelector: enquiryDetail.matterId ? {
            label: enquiryDetail.matterFileNumber + ' (' + enquiryDetail.matterTitle + ')',
            value: '' + enquiryDetail.matterGuid,
            fileNumber: enquiryDetail.matterFileNumber,
            title: enquiryDetail.matterTitle,
            id: enquiryDetail.matterId
        } : null,
        enquiryNotes: enquiryDetail.enquiryNotes,

        otherPartyFirstName: enquiryDetail.otherPartyFirstName,
        otherPartyLastName: enquiryDetail.otherPartyLastName,
        otherPartyNotes: enquiryDetail.otherPartyNotes,
        otherPartyEmail: enquiryDetail.otherPartyEmail,
        otherPartyPhone: enquiryDetail.otherPartyPhone,
        otherPartyMobile: enquiryDetail.otherPartyMobile,
        otherPartyOrganisation: enquiryDetail.otherPartyOrganisation,
        isSavedDirectly: false,
        otherPartyDOB: enquiryDetail.otherPartyDOB,
        otherPartyAddressName: enquiryDetail.otherPartyAddressName,
        otherPartyFloor: enquiryDetail.otherPartyFloor,
        otherPartyNumber: enquiryDetail.otherPartyNumber,
        otherPartyStreet: enquiryDetail.otherPartyStreet,
        otherPartyStreet2: enquiryDetail.otherPartyStreet2,
        otherPartySuburb: enquiryDetail.otherPartySuburb,
        otherPartyState: enquiryDetail.otherPartyState,
        otherPartyPostcode: enquiryDetail.otherPartyPostcode,
        otherPartyCountry: enquiryDetail.otherPartyCountry,

        otherPartyPOBoxType: enquiryDetail.otherPartyPOBoxType,
        otherPartyPOBox: enquiryDetail.otherPartyPOBox,
        otherPartyPostalSuburb: enquiryDetail.otherPartyPostalSuburb,
        otherPartyPostalState: enquiryDetail.otherPartyPostalState,
        otherPartyPostalPostCode: enquiryDetail.otherPartyPostalPostCode,
        otherPartyPostalCountry: enquiryDetail.otherPartyPostalCountry,
        otherPartyNameGuid: enquiryDetail.otherPartyNameGuid,
        otherPartyOrganisationGuid: enquiryDetail.otherPartyOrganisationGuid,
        otherPartyIndividualSelector: otherPartyIndividualSelector,
        isOtherPartyNameSwitched: false,

        isRiskAssessed: enquiryDetail.isRiskAssessed,
        riskLevelSelector: enquiryDetail.riskLevelGuid ? { label: enquiryDetail.riskLevel ?? '', value: enquiryDetail.riskLevelGuid } : null,
        riskNotes: enquiryDetail.riskNotes,

        secondClientFirstName: enquiryDetail.secondClientFirstName,
        secondClientLastName: enquiryDetail.secondClientLastName,
        secondClientRelationshipToEnquirer: enquiryDetail.secondClientRelationshipToEnquirer,
        secondClientGender: enquiryDetail.secondClientGender,
        secondClientSuburb: enquiryDetail.secondClientSuburb,
        secondClientState: enquiryDetail.secondClientState,
        secondClientPostCode: enquiryDetail.secondClientPostCode,
        secondClientIsMarketingConsent: enquiryDetail.secondClientIsMarketingConsent,
        secondClientIsTermsAndConditionsApproved: enquiryDetail.secondClientIsTermsAndConditionsApproved,
        secondClientIsPrivacyStatementProvided: enquiryDetail.secondClientIsPrivacyStatementProvided,
        secondClientIsConflictCheckPerformed: enquiryDetail.secondClientIsConflictCheckPerformed,
        secondClientIsIdentityCheckPerformed: enquiryDetail.secondClientIsIdentityCheckPerformed,
        secondClientIsNoConflict: enquiryDetail.secondClientIsNoConflict,
        secondClientCountry: enquiryDetail.secondClientCountry,
        secondClientAddressName: enquiryDetail.secondClientAddressName,
        secondClientFloor: enquiryDetail.secondClientFloor,
        secondClientNumber: enquiryDetail.secondClientNumber,
        secondClientStreet1: enquiryDetail.secondClientStreet1,
        secondClientStreet2: enquiryDetail.secondClientStreet2,
        secondClientDOB: enquiryDetail.secondClientDOB,
        secondClientPOBoxType: enquiryDetail.secondClientPOBoxType,
        secondClientPOBox: enquiryDetail.secondClientPOBox,
        secondClientPostalSuburb: enquiryDetail.secondClientPostalSuburb,
        secondClientPostalState: enquiryDetail.secondClientPostalState,
        secondClientPostalPostCode: enquiryDetail.secondClientPostalPostCode,
        secondClientPostalCountry: enquiryDetail.secondClientPostalCountry,

        responseText: enquiryDetail.responseText,

        // bot integration
        followUpBotUrl: null,

        // events and campaigns
        // tslint:disable-next-line: max-line-length
        eventAndCampaignSelector: enquiryDetail.eventAndCampaignGuid && enquiryDetail.eventAndCampaignName ? { label: enquiryDetail.eventAndCampaignName, value: enquiryDetail.eventAndCampaignGuid } as EventsAndCampaignIAutoCompleteItem : null,
        eventsAndCampaignNotes: enquiryDetail.eventAndCampaignNotes,

        title: enquiryDetail.title,
        otherPartyTitle: enquiryDetail.otherPartyTitle,

        clientTitle: enquiryDetail.clientTitle,
        clientGuid: enquiryDetail.clientGuid,
        clientEmail: enquiryDetail.clientEmail,
        clientPhone: enquiryDetail.clientPhone,
        clientMobile: enquiryDetail.clientMobile,

        secondClientTitle: enquiryDetail.secondClientTitle,
        secondClientGuid: enquiryDetail.secondClientGuid,
        secondClientEmail: enquiryDetail.secondClientEmail,
        secondClientPhone: enquiryDetail.secondClientPhone,
        secondClientMobile: enquiryDetail.secondClientMobile,

        clientIndividualSelector: clientIndividualSelector,
        isClientNameSwitched: false,
        isSecondClientNameSwitched: false,
        secondClientIndividualSelector: secondClientIndividualSelector,

        customField: reverseCustomFieldConversion(enquiryDetail.customFields),

        clickThroughUrl: enquiryDetail.clickThroughUrl,
        utmSource: enquiryDetail.utmSource,
        utmMedium: enquiryDetail.utmMedium,
        utmCampaign: enquiryDetail.utmCampaign,
        utmContent: enquiryDetail.utmContent,
        utmKeyword: enquiryDetail.utmKeyword,
        googleClickIdentifier: enquiryDetail.googleClickIdentifier,
        facebookClickIdentifier: enquiryDetail.facebookClickIdentifier,
    };
};
